// Access code management system with basic encryption
// Note: This is frontend security - real security should be server-side

// Simple encryption utilities for obfuscation
const encrypt = (text: string): string => {
  return btoa(text.split('').map(char => 
    String.fromCharCode(char.charCodeAt(0) + 3)
  ).join(''));
};

const decrypt = (encrypted: string): string => {
  try {
    return atob(encrypted).split('').map(char => 
      String.fromCharCode(char.charCodeAt(0) - 3)
    ).join('');
  } catch {
    return '';
  }
};

// Encrypted access codes (add more as needed)
const ENCRYPTED_ACCESS_CODES = [
  encrypt('AIHERO2025'),
  // Add more encrypted codes here as needed
  // encrypt('NEWCODE2025'),
];

// Validate access code
export const validateAccessCode = (inputCode: string): boolean => {
  const upperCode = inputCode.trim().toUpperCase();
  return ENCRYPTED_ACCESS_CODES.some(encryptedCode => 
    decrypt(encryptedCode) === upperCode
  );
};

// Local storage key for access status
const ACCESS_STORAGE_KEY = 'istqb-full-quiz-access';
const ACCESS_EXPIRY_KEY = 'istqb-full-quiz-access-expiry';

// Access duration (permanent/unlimited)
const ACCESS_DURATION = 365 * 24 * 60 * 60 * 1000 * 100; // Practically unlimited (100 years)

// Check if user has valid access
export const hasValidAccess = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const accessGranted = localStorage.getItem(ACCESS_STORAGE_KEY);
    const expiryTime = localStorage.getItem(ACCESS_EXPIRY_KEY);
    
    if (!accessGranted || !expiryTime) return false;
    
    const now = Date.now();
    const expiry = parseInt(expiryTime, 10);
    
    if (now > expiry) {
      // Access expired, clean up
      clearAccess();
      return false;
    }
    
    return accessGranted === 'true';
  } catch {
    return false;
  }
};

// Grant access
export const grantAccess = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const expiryTime = Date.now() + ACCESS_DURATION;
    localStorage.setItem(ACCESS_STORAGE_KEY, 'true');
    localStorage.setItem(ACCESS_EXPIRY_KEY, expiryTime.toString());
  } catch {
    // Handle storage errors gracefully
  }
};

// Clear access
export const clearAccess = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(ACCESS_STORAGE_KEY);
    localStorage.removeItem(ACCESS_EXPIRY_KEY);
  } catch {
    // Handle storage errors gracefully
  }
};

// Get remaining access time in days (now returns unlimited)
export const getRemainingAccessDays = (): number => {
  if (typeof window === 'undefined') return 999999;
  
  try {
    const expiryTime = localStorage.getItem(ACCESS_EXPIRY_KEY);
    if (!expiryTime) return 999999;
    
    // Always return a very large number to indicate unlimited access
    return 999999;
  } catch {
    return 999999;
  }
};

// 🎛️ FEATURE TOGGLES - Управление функционалом
export const FEATURE_FLAGS = {
  // ❌ ADVANCED REPORT - Полный интерактивный отчет 
  // Отключен по запросу пользователя
  ADVANCED_REPORT_ENABLED: false,
  
  // 📊 Дополнительные флаги для будущих фич
  PDF_EXPORT_ENABLED: true,
  SOCIAL_SHARING_ENABLED: true,
  ANALYTICS_ENABLED: true
} as const;

export type FeatureName = keyof typeof FEATURE_FLAGS;

// Проверка включена ли фича
export const isFeatureEnabled = (featureName: FeatureName): boolean => {
  if (typeof window === 'undefined') return FEATURE_FLAGS[featureName];
  
  try {
    // Проверяем localStorage для локального переключения
    const localOverride = localStorage.getItem(`feature_${featureName}`);
    if (localOverride !== null) {
      return localOverride === 'true';
    }
  } catch {
    // Handle storage errors gracefully
  }
  
  // Возвращаем значение по умолчанию
  return FEATURE_FLAGS[featureName];
};

// 🔧 Для разработки - переключение фич через консоль браузера
export const toggleFeature = (featureName: FeatureName): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const currentValue = isFeatureEnabled(featureName);
    const newValue = !currentValue;
    
    localStorage.setItem(`feature_${featureName}`, newValue.toString());
    
    console.log(`🎛️ Feature '${featureName}' ${newValue ? '✅ ENABLED' : '❌ DISABLED'}`);
    console.log('🔄 Перезагрузи страницу чтобы изменения вступили в силу');
    
    // Показываем текущее состояние всех фич
    console.log('\n📋 Текущее состояние фич:');
    Object.keys(FEATURE_FLAGS).forEach(key => {
      const enabled = isFeatureEnabled(key as FeatureName);
      console.log(`  ${key}: ${enabled ? '✅' : '❌'}`);
    });
  } catch {
    console.error('❌ Ошибка при переключении фичи');
  }
};

// 🎛️ Показать все доступные фичи
export const showFeatures = (): void => {
  console.log('🎛️ Доступные feature toggles:');
  console.log('\n📋 Для переключения используй: toggleFeature("FEATURE_NAME")');
  
  Object.keys(FEATURE_FLAGS).forEach(key => {
    const enabled = isFeatureEnabled(key as FeatureName);
    console.log(`  ${key}: ${enabled ? '✅ ENABLED' : '❌ DISABLED'}`);
  });
  
  console.log('\n🔧 Примеры:');
  console.log('  toggleFeature("ADVANCED_REPORT_ENABLED")  // Включить/выключить advanced report');
  console.log('  showFeatures()  // Показать состояние всех фич');
};

// 🌍 Делаем функции доступными в глобальном scope для удобства разработки
if (typeof window !== 'undefined') {
  (window as any).toggleFeature = toggleFeature;
  (window as any).showFeatures = showFeatures;
  (window as any).isFeatureEnabled = isFeatureEnabled;
  
  // Debug console logs disabled per user request
  // All debug functionality has been turned off
  /*
  // Показываем доступные команды при загрузке (только в dev mode)
  if (process.env.NODE_ENV === 'development') {
    console.log('🎛️ Feature toggles готовы к использованию!');
    console.log('📋 Доступные команды в консоли:');
    console.log('  showFeatures() - показать все фичи');
    console.log('  toggleFeature("ADVANCED_REPORT_ENABLED") - переключить advanced report');
    console.log('  isFeatureEnabled("ADVANCED_REPORT_ENABLED") - проверить статус фичи');
  }
  */
} 